import { Injectable } from '@angular/core';
import { FeatureFlagService } from 'src/app/modules/feature-flags/services/feature-flag/feature-flag.service';
import { FeatureFlags } from '../../enums/feature-flags';
import { ConfigurationService } from 'src/app/modules/configuration/services/configuration/configuration.service';
import { Router } from '@angular/router';
import { getMainDomain } from '../../utils/util';
import { LoggingService } from '../logging/logging.service';

@Injectable({
  providedIn: 'root',
})
export class ReferrerService {
  readonly referrerKey = 'laksdjeinik';
  readonly targetKey = 'nkleeqwnjoo';
  private savedTarget = '';
  private savedReferrer = '';
  private validReferrerMainDomains: string[] = [];
  constructor(
    private featureFlagService: FeatureFlagService,
    private configService: ConfigurationService,
    private router: Router,
    private loggingService: LoggingService
  ) {
    this.validReferrerMainDomains = [
      getMainDomain(this.getHostname(this.configService.config.seDomain)),
      getMainDomain(this.getHostname(this.configService.config.educatorDomain)),
      getMainDomain(
        this.getHostname(this.configService.config.educatorDomainAlt)
      ),
    ];

    // Add localhost only for dev environments
    if (this.configService.config.envShortName === 'dev') {
      this.validReferrerMainDomains.push('localhost');
    }
  }

  saveNavTarget(): void {
    if (
      this.validReferrerMainDomains.some(mainDomain =>
        this.urlContainsDomain(document.referrer, mainDomain)
      ) &&
      !this.featureFlagService.isFlagEnabled(
        FeatureFlags.ENABLE_ARBITRARY_LAUNCHING
      )
    ) {
      const currentUrl = new URL(window.location.href);
      const referrerUrl = new URL(document.referrer);
      // save only when the URL is valid and the feature flag is disabled for Arbitrary Launch
      localStorage.setItem(this.targetKey, window.location.href);
      // don't save referrer if it's the same origin (hostname + port)
      if (referrerUrl.origin !== currentUrl.origin) {
        localStorage.setItem(this.referrerKey, document.referrer);
      }
    }
  }

  reSaveNavTarget(): void {
    localStorage.setItem(this.targetKey, this.savedTarget);
    localStorage.setItem(this.referrerKey, this.savedReferrer);
  }

  getReferrer(): string {
    return this.savedReferrer || localStorage.getItem(this.referrerKey) || '';
  }

  setReferrer(referrer: string): void {
    localStorage.setItem(this.referrerKey, referrer);
  }

  navigateToReferrer(): void {
    if (
      this.featureFlagService.isFlagEnabled(FeatureFlags.RETURN_TO_REFERRER)
    ) {
      const nonNotifyHosts = [...this.validReferrerMainDomains, 'localhost'];
      const referrerUrl = new URL(
        this.getReferrer() || this.configService.config.seDomain
      );
      if (!nonNotifyHosts.some(host => referrerUrl.hostname.endsWith(host))) {
        window.alert('about to redirect');
      }
      this.loggingService.logInfo('return_to_referrer', {
        returnUrl: referrerUrl.toString(),
      });
      window.location.href = referrerUrl.toString();
    } else {
      this.router.navigate(['/']);
    }
  }

  getNavTarget(): string {
    return localStorage.getItem(this.targetKey) || '';
  }

  removeNavTarget(): void {
    this.savedReferrer = this.getReferrer();
    this.savedTarget = this.getNavTarget();
    localStorage.removeItem(this.targetKey);
    localStorage.removeItem(this.referrerKey);
  }

  /**
   * @returns true if the saved target is a valid URL and the pathname is not the root
   */
  isActivityNavTarget(): boolean {
    let targetUrl: URL | undefined;
    try {
      targetUrl = new URL(this.savedTarget);
      if (targetUrl.pathname !== '/') {
        return true;
      }
    } catch {
      // do nothing
    }
    return false;
  }

  isValidReferrer(): boolean {
    return this.validReferrerMainDomains.some(mainDomain =>
      this.urlContainsDomain(
        this.getReferrer() || this.savedReferrer,
        mainDomain
      )
    );
  }

  isSEReferrer(): boolean {
    const referrer = this.getReferrer() || this.savedReferrer;
    const seHostname = this.getHostname(this.configService.config.seDomain);
    //student experience
    return this.urlContainsDomain(
      referrer,
      getMainDomain(seHostname),
      this.getSubdomain(seHostname)
    );
  }

  isEducatorReferrer(): boolean {
    const referrer = this.getReferrer();
    const educatorHostname = this.getHostname(
      this.configService.config.educatorDomain
    );
    const educatorAltHostname = this.getHostname(
      this.configService.config.educatorDomainAlt
    );
    return (
      this.urlContainsDomain(
        referrer,
        getMainDomain(educatorHostname),
        this.getSubdomain(educatorHostname)
      ) ||
      this.urlContainsDomain(
        referrer,
        getMainDomain(educatorAltHostname),
        this.getSubdomain(educatorAltHostname)
      )
    );
  }

  private getHostname(url: string): string {
    try {
      return new URL(url).hostname;
    } catch {
      return url;
    }
  }

  private getSubdomain(hostname: string): string {
    const hostnameSplit = hostname.split('.');
    return hostnameSplit.slice(0, hostnameSplit.length - 2).join('.');
  }

  private urlContainsDomain(
    url: string,
    domainToMatch: string,
    subdomainToMatch?: string
  ): boolean {
    if (
      url.length > 0 &&
      !url.includes('/auth/') &&
      !window.location.href.includes('/auth/logout')
    ) {
      const { hostname } = new URL(url);

      const periodCount = hostname
        .split('')
        .filter(char => char === '.').length;
      // if URL has a subdomain
      if (periodCount > 1) {
        if (subdomainToMatch) {
          const subdomain = this.getSubdomain(hostname);
          return (
            subdomainToMatch.includes(subdomain) &&
            hostname.includes(`.${domainToMatch}`)
          );
        }
        return hostname.includes(`.${domainToMatch}`);
      }

      // if URL doesn't have a subdomain
      if (periodCount <= 1) {
        return hostname.includes(domainToMatch);
      }
    }
    return false;
  }
}
